import {
    FeatureUserGroup,
    ILocationPermissionsResponse,
    LocationPermissionsUpdateRequest,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { DropdownInlineEditor } from 'components/inline_edit/DropdownInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { LocationUserGroupCell } from './LocationUserGroupCell';

const USER_GROUP_CATEGORIES = {
    [FeatureUserGroup.All]: new Set([
        FeatureUserGroup.GlobalAdmin,
        FeatureUserGroup.Sentry,
        FeatureUserGroup.Sales,
        FeatureUserGroup.Admin,
        FeatureUserGroup.User,
    ]),
    [FeatureUserGroup.Kcf]: new Set([
        FeatureUserGroup.GlobalAdmin,
        FeatureUserGroup.Sentry,
        FeatureUserGroup.Sales,
    ]),
};

const OPTIONS_DISPLAY: Partial<Record<FeatureUserGroup, string>> = {
    [FeatureUserGroup.None]: 'None',
    [FeatureUserGroup.Kcf]: 'KCF',
    [FeatureUserGroup.All]: 'All Users',
};

function checkMatch(
    extractFrom: Set<FeatureUserGroup>,
    toExtract: Set<FeatureUserGroup>,
): { set: Set<FeatureUserGroup>; anyMatch: boolean; allMatch: boolean } {
    const intersection = extractFrom.intersection(toExtract);
    return {
        set: intersection,
        anyMatch: intersection.size > 0,
        allMatch: intersection.size === toExtract.size,
    };
}

/**
 * the API does not return composite user groups (All, Kcf), so we extract them
 * manually (since that's what we want to present to users for this feature)
 */
function loadInitialFeatureUserGroup(
    value: FeatureUserGroup[],
): FeatureUserGroup | 'not-clean-categories' {
    const s = new Set(value);
    const kcf = checkMatch(s, USER_GROUP_CATEGORIES[FeatureUserGroup.Kcf]);
    const all = checkMatch(s, USER_GROUP_CATEGORIES[FeatureUserGroup.All]);

    if (all.allMatch) {
        return FeatureUserGroup.All;
    }

    if (kcf.allMatch) {
        const withoutKcf = s.difference(USER_GROUP_CATEGORIES[FeatureUserGroup.Kcf]);
        if (withoutKcf.size !== 0) {
            return 'not-clean-categories';
        }
        return FeatureUserGroup.Kcf;
    }

    s.delete(FeatureUserGroup.None);
    if (s.size !== 0) {
        return 'not-clean-categories';
    }
    return FeatureUserGroup.None;
}

export type LocationDeskChatUserGroupCellProps = {
    locationId: string;
    data: ILocationPermissionsResponse;
    onChange: () => void;
};

export function LocationDeskChatUserGroupCell({
    locationId,
    data,
    onChange,
}: LocationDeskChatUserGroupCellProps) {
    const { canEditLocationFeaturePermissions } = usePermissions();
    const { showToast } = useNotificationContext();
    const { mutate: updatePermission, isPending } = useMutation({
        meta: { name: 'update-location-permissions' },
        mutationFn: async (value: FeatureUserGroup[]) => {
            await sdClient.updateLocationPermissions({
                id: locationId,
                body: new LocationPermissionsUpdateRequest({
                    ...data,
                    DeskChatAutoReplyEnabledFor: value,
                }),
            });
        },

        onSuccess: () => {
            onChange();
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save location value unit',
            }),
    });

    const initialValue = loadInitialFeatureUserGroup(data.DeskChatAutoReplyEnabledFor);

    if (initialValue === 'not-clean-categories') {
        // somehow, the API claims that the settings are more fine-grained than
        // we allow setting in the UI in the normal case. Expose a control that
        // supports this granularity for this case, so the user can interact
        // with it accurately.
        return (
            <LocationUserGroupCell
                locationId={locationId}
                data={data}
                permsKey="DeskChatAutoReplyEnabledFor"
                onChange={onChange}
            />
        );
    }

    const renderValue = (val: FeatureUserGroup | undefined) =>
        val !== undefined ? OPTIONS_DISPLAY[val] : 'KCF (default)';

    return (
        <DropdownInlineEditor<FeatureUserGroup>
            value={initialValue}
            options={[FeatureUserGroup.None, FeatureUserGroup.Kcf, FeatureUserGroup.All]}
            onConfirm={(v) => {
                if (v !== undefined) {
                    updatePermission([v]);
                }
            }}
            isDisabled={!canEditLocationFeaturePermissions || isPending}
            renderValue={renderValue}
            dropdownConfig={{
                renderItem: ({ item }) => OPTIONS_DISPLAY[item] ?? item,
                renderLabel: ({ value }) => renderValue(value),
                dataKey: (item) => item,
            }}
        />
    );
}
